import {
  EditOutlined,
  GroupAddOutlined,
  VisibilityOutlined,
} from '@mui/icons-material';
import { Box, Switch, Typography } from '@mui/material';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid-premium';
import {
  useUpdateLocationsFundTransferConfigMutation,
  useUpdatePoolLocationFundTransferConfigMutation,
} from 'api/giftcard-ach-service/giftcard-ach-service';
import ACHCopyFromDrawer from 'components/drawers/ach-copy-drawers/ach-copy-from-drawer/ach-copy-from-drawer.component';
import ACHCopyToDrawer from 'components/drawers/ach-copy-drawers/ach-copy-to-drawer/ach-copy-to-drawer.component';
import { useUtilityDrawer } from 'components/drawers/utility-drawer/utility-drawer.hooks';
import { useHasPermission } from 'hooks/authorization.hooks';
import ACHLocationStatusChip from './ach-location-status-chip/ach-location-status-chip.component';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PermissionedGridActionCellItem from 'pages/communications/offers/offers-data-grid/permissioned-grid-action-cell-item/permissioned-grid-action-cell-item.component';
import { useState } from 'react';
import ClientPaginationDataGrid from 'components/client-pagination-data-grid/client-pagination-data-grid.component';
import {
  ACHLocationStatus,
  AchLocationType,
} from 'api/giftcard-ach-service/giftcard-ach-service.types';

type MoneyMovementLocationsSectionProps = {
  title: string;
  transferTypeTitle: string;
  locations: (Partial<AchLocationType> &
    Required<
      Pick<
        AchLocationType,
        'locationId' | 'locationName' | 'overallStatus' | 'fundTransferEnabled'
      >
    >)[];
  'data-testid'?: string;
  isLoading: boolean;
};

const MoneyMovementLocationsSection = ({
  title,
  transferTypeTitle,
  locations,
  isLoading,
  'data-testid': dataTestId,
}: MoneyMovementLocationsSectionProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const enrollPermission = useHasPermission('ACH_PORTAL_ENROLL_LOCATION');
  const isPoolLocationSection = dataTestId === 'PoolLocationSection';
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [updateLocationsFundTransferConfig, { isLoading: isLocationLoading }] =
    useUpdateLocationsFundTransferConfigMutation();
  const [updatePoolLocationFundTransferConfig, { isLoading: isPoolLoading }] =
    useUpdatePoolLocationFundTransferConfigMutation();
  const {
    open: isCopyFromOpen,
    close: closeCopyFrom,
    setOpen: setCopyFromOpen,
  } = useUtilityDrawer();
  const {
    open: isCopyToOpen,
    close: closeCopyTo,
    setOpen: setCopyToOpen,
  } = useUtilityDrawer();

  const openCopyFromDrawer = (locationId: string) => {
    setSelectedLocationId(locationId);
    setCopyFromOpen(true);
  };

  const openCopyToDrawer = (locationId: string) => {
    setSelectedLocationId(locationId);
    setCopyToOpen(true);
  };

  const columns: GridColDef<AchLocationType>[] = [
    {
      field: 'locationName',
      headerName: t(
        'page.giftCards.moneyMovement.sections.accountConfiguration.headers.locationName',
      ),
      flex: 1,
      hideable: false,
      renderCell: params => params.value,
    },
    {
      field: 'overallStatus',
      headerName: t(
        'page.giftCards.moneyMovement.sections.accountConfiguration.headers.accountVerificationStatus',
      ),
      flex: 1,
      renderCell: params => <ACHLocationStatusChip status={params.value} />,
    },
    {
      field: 'fundTransferEnabled',
      headerName: transferTypeTitle,
      flex: 1,
      renderCell: params => {
        const value = params.value;
        return (
          <Switch
            checked={!!value}
            value={!!value ? 'on' : 'off'}
            data-testid="TransferStatusSwitch"
            disabled={params.row.overallStatus === 'UNENROLLED'}
            onChange={() => {
              if (isPoolLocationSection) {
                updatePoolLocationFundTransferConfig({
                  active: !params.row.fundTransferEnabled,
                });
              } else {
                updateLocationsFundTransferConfig({
                  locationId: params.row.locationId,
                  active: !params.row.fundTransferEnabled,
                });
              }
            }}
          />
        );
      },
    },
    {
      field: 'actions',
      type: 'actions',
      getActions: params => {
        if (params.row.overallStatus !== 'UNENROLLED') {
          return [
            <GridActionsCellItem
              key={`view-${params.row.locationId}`}
              icon={<VisibilityOutlined />}
              label={t(
                'page.giftCards.moneyMovement.sections.accountConfiguration.menuItems.view',
              )}
              showInMenu
              data-testid="View.MenuItem"
              onClick={() => {
                navigate(`../../${params.row.locationId}`);
              }}
            />,
            ...(params.row.overallStatus ===
            ACHLocationStatus.PENDING_CERTIFY_BENEFICIAL_OWNERSHIP
              ? [
                  <GridActionsCellItem
                    key={`add-beneficial-owners-${params.row.locationId}`}
                    icon={<GroupAddOutlined />}
                    label={t(
                      'page.giftCards.moneyMovement.sections.accountConfiguration.menuItems.addBeneficialOwners',
                    )}
                    showInMenu
                    data-testid="AddBeneficialOwners.MenuItem"
                    onClick={() => {
                      navigate(
                        `../../${params.row.locationId}/addbeneficialowners/${params.row.externalId}`,
                      );
                    }}
                  />,
                ]
              : []),
            ...(!isPoolLocationSection &&
            params.row.overallStatus === 'VERIFIED'
              ? [
                  <PermissionedGridActionCellItem
                    key={`copy-to-${params.row.locationId}`}
                    permission="ACH_PORTAL_ENROLL_LOCATION"
                    icon={<EditOutlined />}
                    label={t(
                      'page.giftCards.moneyMovement.sections.accountConfiguration.menuItems.copyTo',
                    )}
                    showInMenu
                    data-testid="CopyTo.MenuItem"
                    onClick={() => openCopyToDrawer(params.row.locationId)}
                  />,
                ]
              : []),
          ];
        }
        if (enrollPermission)
          return [
            <PermissionedGridActionCellItem
              key={`enroll-${params.row.locationId}`}
              permission="ACH_PORTAL_ENROLL_LOCATION"
              icon={<EditOutlined />}
              label={t(
                'page.giftCards.moneyMovement.sections.accountConfiguration.menuItems.enroll',
              )}
              showInMenu
              data-testid="Enroll.MenuItem"
              onClick={() => {
                navigate(`../../${params.row.locationId}/enroll`);
              }}
            />,
            ...(!isPoolLocationSection
              ? [
                  <PermissionedGridActionCellItem
                    key={`copy-from-${params.row.locationId}`}
                    permission="ACH_PORTAL_ENROLL_LOCATION"
                    icon={<EditOutlined />}
                    label={t(
                      'page.giftCards.moneyMovement.sections.accountConfiguration.menuItems.copyFrom',
                    )}
                    showInMenu
                    data-testid="CopyFrom.MenuItem"
                    onClick={() => openCopyFromDrawer(params.row.locationId)}
                  />,
                ]
              : []),
          ];
        return [];
      },
    },
  ];

  return (
    <Box data-testid={dataTestId}>
      <Typography variant="h4">{title}</Typography>
      <ClientPaginationDataGrid
        getRowId={loc => loc.locationId}
        columns={columns}
        rows={locations}
        isLoading={isLocationLoading || isPoolLoading || isLoading}
        pagination={!isPoolLocationSection}
        hideFooter={isPoolLocationSection}
      />
      {!isPoolLocationSection ? (
        <>
          <ACHCopyFromDrawer
            open={isCopyFromOpen}
            close={closeCopyFrom}
            locationId={selectedLocationId}
          />
          <ACHCopyToDrawer
            open={isCopyToOpen}
            close={closeCopyTo}
            locationId={selectedLocationId}
          />
        </>
      ) : null}
    </Box>
  );
};

export default MoneyMovementLocationsSection;
